import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { CommunitySettings } from "@circle-react/types/CommunitySettings";
import { noop } from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";

import {
  reactQueryGet,
  reactQueryPatch,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

const REQUEST_KEY = ["community-setting", "show"];

export const useCommunitySettingShow = () =>
  useQuery({
    queryKey: REQUEST_KEY,
    queryFn: () => {
      const url = internalApi.communities.communitySettings.index();
      return reactQueryGet<{ community_settings: CommunitySettings }>(url);
    },
  });

const updateCommunitySettings = ({ data, updates }: any) => {
  const url = internalApi.communities.communitySettings.update({
    communitySettingId: data.community_settings.id,
  });

  const payload = { community_setting: updates };

  return reactQueryPatch(url, payload);
};

export const useUpdateCommunitySetting = ({
  onMutate = noop,
  onSuccess = noop,
  onError = noop,
  onSettled = noop,
} = {}) => {
  const { refetch } = usePunditUserContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCommunitySettings,
    onMutate: ({ data }) => {
      const previousData = queryClient.getQueryData(REQUEST_KEY);
      queryClient.setQueryData(REQUEST_KEY, data);
      onMutate && onMutate();
      return { previousData, data };
    },
    onSuccess: async data => {
      await refetch();
      queryClient.setQueryData(REQUEST_KEY, data);
      onSuccess && onSuccess();
    },
    onError: onError,
    onSettled: onSettled,
  });
};

export const useIsFeatureFlagEnabled = (
  featureFlag: keyof CommunitySettings,
) => {
  const { currentCommunitySettings } = usePunditUserContext();
  return Boolean(currentCommunitySettings?.[featureFlag]);
};
